import type { NetworkTelemetryMeta, NetworkTelemetryRow } from "@features/reports/api";
import { Card, Space, Statistic, theme } from "antd";
import { useMemo } from "react";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { formatNumber } from "./formatNumber";
import type { CoefficientMode, NetworkTelemetryMetric } from "./store";
import { useNetworkTelemetryChartModel } from "./useNetworkTelemetryChartModel";

type Props = {
  rows: NetworkTelemetryRow[];
  metric: NetworkTelemetryMetric;
  coefficient: CoefficientMode;
  meta?: NetworkTelemetryMeta;
};

const NetworkTelemetryChart = ({ rows, metric, coefficient, meta }: Props) => {
  const { token } = theme.useToken();
  const { metricLabel, chartData, hasTrend, max, last, trendPerHour, isScaled } =
    useNetworkTelemetryChartModel({ rows, metric, coefficient, meta });

  const colors = useMemo(
    () => ({
      value: isScaled ? token.colorSuccess : token.colorPrimary,
      trend: token.colorWarning,
      trendFill: token.colorWarningBg,
      background: token.colorBgContainer,
      grid: token.colorBorderSecondary,
      text: token.colorText,
    }),
    [token, isScaled],
  );

  const coeffLabel = isScaled ? "С Кт × Кн" : "Без Кт × Кн";

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <Space wrap>
        <Card size="small" style={{ minWidth: 220 }}>
          <Statistic title={`Максимум (${metricLabel})`} value={max ?? 0} precision={3} />
        </Card>
        <Card size="small" style={{ minWidth: 220 }}>
          <Statistic title={`Последнее (${metricLabel})`} value={last ?? 0} precision={3} />
        </Card>
        <Card size="small" style={{ minWidth: 220 }}>
          <Statistic title="Точек" value={rows.length} />
        </Card>
        <Card size="small" style={{ minWidth: 220 }}>
          <Statistic title={`Тренд (${metricLabel}/час)`} value={trendPerHour ?? 0} precision={3} />
        </Card>
      </Space>

      <div style={{ width: "100%", height: 420, minWidth: 0 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grid} />
            <XAxis dataKey="time" tick={{ fill: colors.text }} />
            <YAxis tick={{ fill: colors.text }} />
            <Tooltip
              formatter={(value: unknown) =>
                typeof value === "number" ? formatNumber(value) : "—"
              }
              contentStyle={{ backgroundColor: colors.background, borderColor: colors.grid }}
            />
            <Legend />
            {hasTrend && chartData.some((d) => d.trendUpper != null) && (
              <Area
                type="monotone"
                dataKey="trendUpper"
                stroke="none"
                fill={colors.trend}
                fillOpacity={0.15}
                name="Коридор тренда"
                isAnimationActive={false}
              />
            )}
            {hasTrend && chartData.some((d) => d.trendLower != null) && (
              <Area
                type="monotone"
                dataKey="trendLower"
                stroke="none"
                fill={colors.background}
                fillOpacity={1}
                legendType="none"
                isAnimationActive={false}
              />
            )}
            <Line
              type="monotone"
              dataKey="value"
              name={`${metricLabel} (${coeffLabel})`}
              stroke={colors.value}
              dot={false}
              isAnimationActive={false}
            />
            {hasTrend && (
              <Line
                type="monotone"
                dataKey="trend"
                name="Тренд"
                stroke={colors.trend}
                strokeDasharray="6 4"
                dot={false}
                isAnimationActive={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Space>
  );
};

export default NetworkTelemetryChart;
